import { BookOpen, ChevronRight, Home, MapPin, Search, Star, TrendingUp } from 'lucide-react'
import { useState } from 'react'
import { PoweredByFooter } from '@/components/PoweredByFooter'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'

interface PublicHomeScreenProps {
  onNavigate: (screen: string) => void
}

const featuredAgents = [
  { id: 1, name: 'Sarah Mitchell', area: 'Auckland Central', rating: 4.9, sales: 87, photo: 'S' },
  { id: 2, name: 'James Wong', area: 'Wellington', rating: 4.8, sales: 65, photo: 'J' },
  { id: 3, name: 'Emma Thompson', area: 'Christchurch', rating: 4.9, sales: 92, photo: 'E' },
]

const marketSnapshots = [
  { region: 'Auckland', median: '$1.02M', change: '+2.1%', positive: true },
  { region: 'Wellington', median: '$845K', change: '+1.5%', positive: true },
  { region: 'Canterbury', median: '$695K', change: '-0.8%', positive: false },
]

const guides = [
  {
    id: 'buying',
    title: "First Home Buyer's Guide",
    description: 'Everything you need to know',
    icon: Home,
  },
  {
    id: 'selling',
    title: 'Selling Your Property',
    description: 'Get the best price',
    icon: TrendingUp,
  },
  {
    id: 'process',
    title: 'The NZ Property Process',
    description: 'Step-by-step guide',
    icon: BookOpen,
  },
]

export function PublicHomeScreen({ onNavigate }: PublicHomeScreenProps) {
  const [searchQuery, setSearchQuery] = useState('')

  return (
    <div className="flex flex-col min-h-full pb-4">
      {/* Search Hero */}
      <div className="bg-gradient-reinz px-4 py-8 -mx-4 -mt-4 mb-6">
        <h1 className="text-xl font-semibold text-primary-foreground mb-2">
          Find Your Perfect Agent
        </h1>
        <p className="text-primary-foreground/70 text-sm mb-4">
          Connect with trusted, licensed real estate professionals
        </p>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search by suburb or city..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 bg-background text-foreground"
          />
        </div>

        <div className="flex gap-2 mt-3 flex-wrap">
          {['Auckland', 'Wellington', 'Christchurch', 'Hamilton'].map((city) => (
            <button
              key={city}
              onClick={() => onNavigate('agents')}
              className="px-3 py-1.5 text-xs bg-primary-foreground/20 text-primary-foreground rounded-full hover:bg-primary-foreground/30 transition-colors"
            >
              {city}
            </button>
          ))}
        </div>
      </div>

      {/* Featured Agents */}
      <section className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-foreground">Featured Agents</h2>
          <Button
            variant="ghost"
            size="sm"
            className="text-primary text-xs h-auto py-1"
            onClick={() => onNavigate('agents')}
          >
            View all
          </Button>
        </div>

        <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
          {featuredAgents.map((agent) => (
            <Card
              key={agent.id}
              className="p-4 min-w-[200px] cursor-pointer card-interactive"
              onClick={() => onNavigate('agents')}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold">
                  {agent.photo}
                </div>
                <div>
                  <p className="font-medium text-foreground text-sm">{agent.name}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {agent.area}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1 text-amber-500">
                  <Star className="w-3 h-3 fill-current" />
                  {agent.rating}
                </span>
                <span className="text-muted-foreground">{agent.sales} sales</span>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* Market Snapshots */}
      <section className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Market Snapshot
          </h2>
          <Button
            variant="ghost"
            size="sm"
            className="text-primary text-xs h-auto py-1"
            onClick={() => onNavigate('market')}
          >
            Full report
          </Button>
        </div>

        <Card className="p-4">
          <div className="space-y-3">
            {marketSnapshots.map((market) => (
              <div
                key={market.region}
                className="flex items-center justify-between py-2 border-b border-border last:border-0"
              >
                <span className="font-medium text-foreground">{market.region}</span>
                <div className="text-right">
                  <p className="font-semibold text-foreground">{market.median}</p>
                  <p className={`text-xs ${market.positive ? 'text-emerald-600' : 'text-red-500'}`}>
                    {market.change}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-3 text-center">
            Based on REINZ data • Updated monthly
          </p>
        </Card>
      </section>

      {/* Buying & Selling Guides */}
      <section className="mb-6">
        <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <BookOpen className="w-4 h-4" />
          Guides & Resources
        </h2>

        <div className="space-y-3">
          {guides.map((guide) => {
            const Icon = guide.icon
            return (
              <Card
                key={guide.id}
                className="p-4 cursor-pointer card-interactive"
                onClick={() => onNavigate('guides')}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-foreground">{guide.title}</p>
                    <p className="text-xs text-muted-foreground">{guide.description}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </Card>
            )
          })}
        </div>
      </section>

      {/* Trust Banner */}
      <Card className="p-4 bg-secondary/50 mb-4">
        <p className="text-sm text-foreground font-medium text-center mb-2">
          Why Choose a REINZ Member Agent?
        </p>
        <p className="text-xs text-muted-foreground text-center">
          All REINZ members are licensed, trained, and committed to professional standards. Your
          peace of mind is our priority.
        </p>
      </Card>

      <PoweredByFooter />
    </div>
  )
}
